import React from "react";
import { useNavigate } from "react-router-dom";
import { Ticket, Megaphone, WhatsappLogo, QrCode, EnvelopeOpen, ChartLineUp, Gear, Sparkle, ArrowRight, ShieldCheck, Wallet, Bank } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";

const FEATURES = [
  { icon: Ticket, title: "Ticketing", desc: "Multi-city, multi-date events with tiered categories, coupons, waitlists and instant QR tickets." },
  { icon: Megaphone, title: "Marketing", desc: "Promote events and run campaigns to reach the right audience and fill seats faster." },
  { icon: WhatsappLogo, title: "WhatsApp & SMS", desc: "Automated, AI-reviewed booking, reminder and update messages your guests actually read." },
  { icon: QrCode, title: "QR Entry", desc: "Fast, secure check-in with category-aware checkpoints and role-based scanners." },
  { icon: EnvelopeOpen, title: "RSVP & Invitations", desc: "Public, RSVP, invite-only and approval events with a full invitee workflow." },
  { icon: ChartLineUp, title: "Analytics", desc: "Live sales, attendance, spend and settlement insights — plus AI answers to your questions." },
  { icon: Gear, title: "Event Operations", desc: "Checkers, checkpoints, deadlines, refunds and settlements — all in one console." },
  { icon: Sparkle, title: "AI Assistance", desc: "AI drafts events, optimizes media, reviews messages and simplifies every workflow." },
  { icon: Wallet, title: "Services Wallet", desc: "Prepaid balance for messaging, QR and marketing with transparent, admin-set rates." },
  { icon: Bank, title: "Settlements", desc: "Clean, separate ticket-revenue payouts with request → transfer → completed tracking." },
];

export default function BecomeOrganizer() {
  const navigate = useNavigate();
  const go = () => navigate("/login?mode=register&role=organizer");
  return (
    <div className="min-h-screen bg-background">
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-primary/10 via-background to-navy/10" />
        <div className="mx-auto max-w-6xl px-4 md:px-6 py-20 md:py-28">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-5">
              <Sparkle weight="fill" size={14} /> For event organizers
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]">
              Everything you need to run <span className="text-primary">unforgettable events</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl">
              Sell tickets, invite guests, message attendees, scan entries and get paid — powered end-to-end by AI. EventVexa is your complete event operating system.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={go} className="rounded-full h-12 px-7 text-base" data-testid="signup-organizer-btn">
                Sign up as an Organizer <ArrowRight className="ml-2" weight="bold" />
              </Button>
              <Button variant="outline" onClick={() => navigate("/discover")} className="rounded-full h-12 px-6 text-base">
                Explore events first
              </Button>
            </div>
            <p className="mt-4 text-xs text-muted-foreground inline-flex items-center gap-1.5">
              <ShieldCheck size={14} className="text-emerald-600" /> Verified onboarding · GST/PAN compliant · Secure payouts
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 md:px-6 py-16 md:py-20">
        <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight mb-3">One platform, every service</h2>
        <p className="text-muted-foreground mb-10 max-w-2xl">From the first draft to final settlement — here's what you get the moment you sign up.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f) => (
            <div key={f.title} className="rounded-2xl border border-border bg-card p-6 transition-colors hover:border-primary/40" data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              <span className="w-11 h-11 grid place-items-center rounded-xl bg-primary/10 text-primary"><f.icon size={22} weight="fill" /></span>
              <h3 className="font-heading text-lg font-semibold mt-4">{f.title}</h3>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 md:px-6 pb-24">
        <div className="rounded-3xl bg-foreground text-background p-10 md:p-14 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Ready to host your first event?</h2>
          <p className="mt-3 text-background/70 max-w-xl mx-auto">Get verified in minutes and publish today. AI helps you every step of the way.</p>
          <Button onClick={go} className="rounded-full h-12 px-8 text-base mt-7 bg-background text-foreground hover:bg-background/90" data-testid="signup-organizer-cta">
            Sign up as an Organizer <ArrowRight className="ml-2" weight="bold" />
          </Button>
        </div>
      </section>
    </div>
  );
}
